// Only authoring helpers. The existing sender/parser/normalizer remain authoritative for sending.
export const variableKeys = text => [
  ...new Set([...text.matchAll(/\{\{([^{}]+)\}\}/g)].map(match => match[1])),
];

export const emptyDraft = () => ({
  name: '',
  language: 'pt_BR',
  category: 'UTILITY',
  parameter_format: 'POSITIONAL',
  components: [{ type: 'BODY', text: '' }],
});

// jsonb does not preserve object-key order; array/component order is significant.
export const draftFingerprint = value =>
  JSON.stringify(value, (_, item) =>
    item && typeof item === 'object' && !Array.isArray(item)
      ? Object.fromEntries(
          Object.keys(item)
            .sort()
            .map(key => [key, item[key]])
        )
      : item
  );

export function exampleValue(component, key, format) {
  const prefix = component.type.toLowerCase();
  if (format === 'NAMED') {
    return (
      component.example?.[`${prefix}_text_named_params`]?.find(
        item => item.param_name === key
      )?.example || ''
    );
  }
  const values = component.example?.[`${prefix}_text`];
  return (
    (prefix === 'body'
      ? values?.[0]?.[Number(key) - 1]
      : values?.[Number(key) - 1]) || ''
  );
}

export function setExample(component, key, value, format) {
  const prefix = component.type.toLowerCase();
  const keys = variableKeys(component.text);
  const values = keys.map(item => ({
    param_name: item,
    example: item === key ? value : exampleValue(component, item, format),
  }));
  if (format === 'NAMED') {
    component.example = { [`${prefix}_text_named_params`]: values };
  } else {
    const ordered = keys.map(
      (_, index) =>
        values.find(item => item.param_name === String(index + 1))?.example ||
        ''
    );
    component.example = {
      [`${prefix}_text`]: prefix === 'body' ? [ordered] : ordered,
    };
  }
}

export function validateDraft(
  draft,
  submitting = false,
  mediaUploadPresent = false
) {
  const errors = [];
  if (!/^[a-z0-9_]{1,512}$/.test(draft.name)) errors.push('name');
  if (!/^[a-z]{2,3}(?:_[A-Z]{2})?$/.test(draft.language))
    errors.push('language');
  draft.components.forEach(component => {
    const field = component.type.toLowerCase();
    if (component.type === 'HEADER' && component.format !== 'TEXT') {
      if (submitting && !mediaUploadPresent)
        errors.push('header.media_example_unavailable');
    } else if (component.type !== 'BUTTONS') {
      const { text } = component;
      const keys = variableKeys(text);
      const alternateExampleKey =
        draft.parameter_format === 'NAMED'
          ? `${field}_text`
          : `${field}_text_named_params`;
      if (component.example?.[alternateExampleKey] !== undefined)
        errors.push(`${field}.examples`);
      if (
        text.length > 4096 ||
        /[{}]/.test(text.replace(/\{\{[^{}]+\}\}/g, ''))
      )
        errors.push(field);
      if (submitting && component.type === 'BODY' && !text.trim())
        errors.push(field);
      if (component.type === 'FOOTER' && keys.length) errors.push(field);
      const valid =
        draft.parameter_format === 'NAMED'
          ? keys.every(key => /^[a-z][a-z0-9_]*$/.test(key))
          : keys.every((_, index) => keys.includes(String(index + 1)));
      if (!valid) errors.push(field);
      if (
        submitting &&
        keys.some(
          key => !exampleValue(component, key, draft.parameter_format).trim()
        )
      )
        errors.push(`${field}.examples`);
    } else {
      const { buttons } = component;
      if (!buttons.length || buttons.length > 3) errors.push('buttons');
      if (
        new Set(
          buttons.map(button =>
            button.type === 'QUICK_REPLY' ? 'reply' : 'action'
          )
        ).size > 1
      )
        errors.push('buttons.combination');
      buttons.forEach(button => {
        if (!button.text || button.text.length > 25 || /[{}]/.test(button.text))
          errors.push('buttons.text');
        if (button.type === 'URL') {
          const url = typeof button.url === 'string' ? button.url : '';
          const variables = variableKeys(url);
          if (variables.length) {
            let parsedUrl;
            try {
              parsedUrl = new URL(url);
            } catch {
              parsedUrl = null;
            }
            if (
              variables.length !== 1 ||
              variables[0] !== '1' ||
              url.length > 2000 ||
              !/^https:\/\/[^/?#\s{}]+[/?#][^\s{}]*\{\{1\}\}$/.test(url) ||
              !parsedUrl?.hostname ||
              parsedUrl.username ||
              parsedUrl.password ||
              Object.keys(button).some(
                key => !['type', 'text', 'url', 'example'].includes(key)
              )
            )
              errors.push('buttons.url');
            if (
              !Array.isArray(button.example) ||
              button.example.length !== 1 ||
              typeof button.example[0] !== 'string' ||
              !button.example[0].trim()
            )
              errors.push('buttons.example');
          } else {
            if (!/^https:\/\/[^\s{}]+$/.test(url) || url.length > 2000)
              errors.push('buttons.url');
            if (button.example !== undefined) errors.push('buttons.example');
            if (
              Object.keys(button).some(
                key => !['type', 'text', 'url'].includes(key)
              )
            )
              errors.push('buttons');
          }
        }
        if (
          button.type === 'PHONE_NUMBER' &&
          !/^\+[1-9][0-9]{6,14}$/.test(button.phone_number)
        )
          errors.push('buttons.phone_number');
      });
    }
  });
  return [...new Set(errors)];
}

export function updateButtonUrl(button, url) {
  button.url = url;
  if (variableKeys(url).length) {
    if (!Array.isArray(button.example) || button.example.length !== 1)
      button.example = [''];
  } else {
    delete button.example;
  }
}

export function setButtonUrlExample(button, value) {
  button.example = [value];
}

export function draftPreview(draft) {
  return {
    ...draft,
    // Render buttons separately so the native text preview retains header/footer.
    components: draft.components
      .filter(component => component.type !== 'BUTTONS')
      .map(component => ({
        ...component,
        text: component.text?.replace(
          /\{\{([^{}]+)\}\}/g,
          (_, key) =>
            exampleValue(component, key, draft.parameter_format) || `[${key}]`
        ),
      })),
  };
}
